import React from 'react';
import { withSize } from 'react-sizeme';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { defineMessages, useIntl } from 'react-intl';

import { PAYMENT_FREQUENCIES } from '@ratehub/documents/mtg-journey-document/stable';
import { can_amortization_table } from '@ratehub/mortgage-calculations';
import {
    HIGHCHART_TYPES,
    Highchart,
    formatDollars,
    getFormattedNumber,
} from '@ratehub/base-ui';

import getPaymentFrequencyAndDivisor from '../functions/getPaymentFrequencyAndDivisor';
import AmortizationTable from './AmortizationTable';


function AmortizationSchedule({
    totalMortgage = null,
    mortgageRate = null,
    termInYears = null,
    amortization = null,
    paymentFrequency,
    isTableIncluded = true,
    size,
    ...otherProps
}) {
    const intl = useIntl();

    const amortizationSchedule = getAmortizationSchedule({
        totalMortgage,
        mortgageRate,
        amortization,
        paymentFrequency,
    });

    return (
        <div
            {...otherProps}
            data-test-name="amortization-schedule"
        >
            <Highchart
                series={getSeriesFromSchedule(amortizationSchedule, intl)}
                chart={{
                    type: HIGHCHART_TYPES.COLUMN,
                    height: 600, // medium
                }}
                plotOptions={{
                    series: {
                        stacking: 'normal',
                    },
                }}
                yAxis={[
                    {
                        title: {
                            text: intl.formatMessage(MESSAGES.SERIES_NAME_BALANCE),
                            enabled: true,
                        },
                        labels: {
                            formatter: yAxisLabelFormatter(intl),
                        },
                    },
                    {
                        title: {
                            text: intl.formatMessage(MESSAGES.AXIS_TITLE_PAYMENT),
                            enabled: true,
                        },
                        labels: {
                            formatter: yAxisLabelFormatter(intl),
                        },
                        opposite: true,
                    },
                ]}
                tooltip={{
                    formatter: tooltipFormatter(intl),
                }}
            />

            <If condition={isTableIncluded}>
                <AmortizationTable
                    amortizationSchedule={amortizationSchedule}
                    termInYears={termInYears}
                />
            </If>
        </div>
    );
}

AmortizationSchedule.propTypes = {
    totalMortgage: PropTypes.number,
    mortgageRate: PropTypes.number,
    termInYears: PropTypes.number,
    amortization: PropTypes.number,
    paymentFrequency: PropTypes.oneOf(
        Object.values(PAYMENT_FREQUENCIES),
    ).isRequired,
    isTableIncluded: PropTypes.bool,
    size: PropTypes.object.isRequired,
};

function getAmortizationSchedule({ totalMortgage, mortgageRate, amortization, paymentFrequency }) {
    const { frequency, divisor } = getPaymentFrequencyAndDivisor(paymentFrequency);
    return can_amortization_table(totalMortgage, (mortgageRate / 100), amortization, frequency, divisor);
}

function getSeriesFromSchedule(amortizationSchedule, intl) {
    const balanceSeries = [];
    const principalSeries = [];
    const interestSeries = [];

    amortizationSchedule.forEach((schedule, index) => {
        const year = dayjs().year() + index;
        const yearAsMS = dayjs().year(year).valueOf();

        balanceSeries.push([ yearAsMS, Math.round(schedule.balance) ]);
        principalSeries.push([ yearAsMS, Math.round(schedule.principal) ]);
        interestSeries.push([ yearAsMS, Math.round(schedule.interest) ]);
    });

    return [
        {
            name: intl.formatMessage(MESSAGES.SERIES_NAME_INTEREST),
            pointStart: dayjs().year(),
            data: interestSeries,
            type: HIGHCHART_TYPES.COLUMN,
            yAxis: 1,
        },
        {
            name: intl.formatMessage(MESSAGES.SERIES_NAME_PRINCIPAL),
            data: principalSeries,
            pointStart: dayjs().year(),
            type: HIGHCHART_TYPES.COLUMN,
            yAxis: 1,
        },
        {
            name: intl.formatMessage(MESSAGES.SERIES_NAME_BALANCE),
            data: balanceSeries,
            pointStart: dayjs().year(),
            type: HIGHCHART_TYPES.LINE,
            yAxis: 0,
        },
    ];
}

function yAxisLabelFormatter(intl) {
    return function() {
        return formatDollars(this.value, intl);
    };
}

function tooltipFormatter(intl) {
    return function() {
        const title = this.point.series.name;
        const xValue = dayjs(this.x).format('YYYY');
        const yValue = getFormattedNumber(this.y, 'CAD0', intl);

        return `
            <div class="layout-condensed">
                <div class="layout-condensed rh-text-align-center">
                    <div class="text-row weight-regular">${title} ${xValue}:</div>
                    <div class="text-row weight-medium">${yValue}</div>
                </div>
            </div>
        `;
    };
}

export const MESSAGES = defineMessages({
    SERIES_NAME_INTEREST: {
        id: 'web-components.chart-amortization-rate.series-name-interest',
        defaultMessage: 'Interest',
    },
    SERIES_NAME_PRINCIPAL: {
        id: 'web-components.chart-amortization-rate.series-name-principal',
        defaultMessage: 'Principal',
    },
    SERIES_NAME_BALANCE: {
        id: 'web-components.chart-amortization-rate.series-name-balance',
        defaultMessage: 'Balance',
    },

    AXIS_TITLE_PAYMENT: {
        id: 'web-components.chart-amortization-rate.axis-title-payment',
        defaultMessage: 'Payment',
    },
});

export default withSize({ noPlaceholder: true })(observer(AmortizationSchedule));
